/**
 * Typed views over the tables that drive skill visuals: states.txt (in 1.07 only the state
 * names — their behaviour is engine code) and Overlay.txt (the animated effects drawn on units:
 * aura rings, curse marks, armor shells, cast flashes).
 */
import type { ExcelTable } from '@d2/formats';

/** Character screen values a state colours (1.10+ states.txt `<x>blue` / `<x>red`). */
export const SHEET_STATS = ['att', 'dam', 'arm', 'rf', 'rl', 'rc', 'rp', 'stambar'] as const;
export type SheetStat = (typeof SHEET_STATS)[number];
export type SheetColors = Partial<Record<SheetStat, 'blue' | 'red'>>;

export interface StateDef {
  /** Row index = state id used on the wire. */
  id: number;
  /** e.g. "STATE_AMPLIFYDAMAGE". */
  name: string;
  /** Values shown blue (raised) or red (lowered) on the character screen while the state holds. */
  sheet?: SheetColors;
  /**
   * 1.10+ only (undefined where the table has no such columns: 1.07–1.09 compiled overlays into
   * the executable): Overlay.txt names in `overlay1..4`, drawn while the state holds.
   */
  overlays?: string[];
  /** 1.10+ `nooverlays`: the client's state function picks the overlays (bone armor, maul, feral rage, cyclone armor), not the columns. */
  noOverlays?: boolean;
  /** 1.10+ `castoverlay`: played once when the state is put on a unit. */
  castOverlay?: string;
  /** 1.10+ `removerlay`: played once when the state ends. */
  removeOverlay?: string;
  /** `gfxtype`/`gfxclass`: drawn as hero class `cls` (type 2, charstats order) or monstats row `cls` (type 1). */
  gfx?: { type: number; cls: number };
}

export interface OverlayDef {
  /** Row index = overlay id used on the wire. */
  id: number;
  name: string;
  /** Archive file under data\global\overlays\ (without extension). */
  file: string;
  frames: number;
  /** Drawn behind the unit (the back half of a ring) instead of in front. */
  preDraw: boolean;
  xOffset: number;
  yOffset: number;
  /** Vertical offset per unit height class (OverlayHeight in monstats; players use 1). */
  height: [number, number, number, number];
  /** Animation speed in sixteenths of a frame per 25 Hz tick (16 = one frame per tick). */
  animRate: number;
  loopWait: number;
  /** DCC draw effect (3 = additive). */
  trans: number;
  radius: number;
  rgb: [number, number, number];
  numDirections: number;
}

export function buildStates(t: ExcelTable): StateDef[] {
  const out: StateDef[] = [];
  let i = 0;
  for (const r of t) {
    const id = i++;
    const name = r.str('state');
    if (!name) continue;
    const sheet: SheetColors = {};
    for (const k of SHEET_STATS) {
      if (r.int(`${k}red`)) sheet[k] = 'red';
      else if (r.int(`${k}blue`)) sheet[k] = 'blue';
    }
    const def: StateDef = Object.keys(sheet).length ? { id, name, sheet } : { id, name };
    if (r.has('overlay1')) {
      def.overlays = [...new Set(['overlay1', 'overlay2', 'overlay3', 'overlay4'].map((c) => r.str(c)).filter((v) => v && v !== '0'))];
      if (r.int('nooverlays')) def.noOverlays = true;
      const cast = r.str('castoverlay'), rem = r.str('removerlay');
      if (cast && cast !== '0') def.castOverlay = cast;
      if (rem && rem !== '0') def.removeOverlay = rem;
    }
    // gfxtype/gfxclass: while the state holds the unit is drawn as a hero class (2) or a monster
    // (1). Tables without the columns (1.07–1.09) have the Valkyrie's in the executable; her
    // monsters\VK sprites are blank placeholders on every disc.
    if (r.has('gfxtype')) {
      const type = r.int('gfxtype');
      if (type) def.gfx = { type, cls: r.int('gfxclass') };
    } else if (name.toLowerCase() === 'valkyrie') def.gfx = { type: 2, cls: 0 };
    out[id] = def;
  }
  return out;
}

export function buildOverlays(t: ExcelTable): OverlayDef[] {
  const out: OverlayDef[] = [];
  let i = 0;
  for (const r of t) {
    const id = i++;
    const name = r.str('overlay');
    if (!name) continue;
    out[id] = {
      id,
      name,
      file: r.str('Filename'),
      frames: r.int('Frames', 1),
      preDraw: r.bool('PreDraw'),
      xOffset: r.int('Xoffset'),
      yOffset: r.int('Yoffset'),
      height: [r.int('Height1'), r.int('Height2'), r.int('Height3'), r.int('Height4')],
      animRate: r.int('AnimRate', 16),
      loopWait: r.int('LoopWaitTime'),
      trans: r.int('Trans'),
      radius: r.int('Radius'),
      rgb: [r.int('Red'), r.int('Green'), r.int('Blue')],
      numDirections: r.int('NumDirections', 1),
    };
  }
  return out;
}
